import { Badge } from "@/shared/ui/badge";
import { FileText, Info, Package, QrCode, Zap } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { getProcessingStatusColor, getProcessingStatusLabel } from "@/features/assets/services/statusColors";

function formatAssetDate(value?: string | null) {
  if (!value) return 'N/A';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

interface AssetHeroHeaderProps {
  asset: Asset;
  qrImageUrl: string | null;
  isTransitioning: boolean;
}

export function AssetHeroHeader({ asset, qrImageUrl, isTransitioning }: AssetHeroHeaderProps) {
  const coreSpecs = [
    { label: 'Manufacturer', value: asset.manufacturer || 'N/A', icon: Package },
    { label: 'Model Number', value: asset.model_number || 'N/A', icon: FileText },
    { label: 'Model Name', value: asset.model_name || 'N/A', icon: FileText },
    { label: 'Manufacture Date', value: formatAssetDate(asset.manufacture_date_string ?? asset.manufacture_date), icon: Info },
    { label: 'Estimated Age', value: asset.estimated_age_years == null ? 'N/A' : `${asset.estimated_age_years} years`, icon: Info },
    { label: 'Serial Number', value: asset.serial_number || 'N/A', icon: Info },
    { label: 'Category', value: asset.category || 'N/A', icon: Zap },
  ];

  return (
    <div className={cn(
      "relative bg-gradient-to-br from-primary/10 via-primary/5 to-background border-b flex-shrink-0 transition-opacity duration-100",
      isTransitioning && "opacity-0"
    )}>
      {/* Subtle gradient accent */}
      <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent pointer-events-none" />

      <div className="relative px-6 py-4">
        <div className="flex flex-col space-y-0 text-center sm:text-left">
          <div className="flex items-start justify-between gap-4">
            {/* Left: Asset Identity */}
            <div className="flex-1 min-w-0 space-y-2">
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <h2 className="text-xl font-bold tracking-tight mb-1.5 text-foreground">
                    {asset.name}
                  </h2>
                  <div className="flex flex-wrap items-center gap-2 text-sm">
                    {asset.category && (
                      <div className="flex items-center gap-1.5">
                        <div className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
                        <span className="text-primary font-medium">{asset.category}</span>
                      </div>
                    )}
                    {asset.processing_status && (
                      <Badge className={cn("text-[11px]", getProcessingStatusColor(asset.processing_status))}>
                        {getProcessingStatusLabel(asset.processing_status)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right: QR Code */}
            <div className="flex items-start gap-3 flex-shrink-0">
              <div className="glass-panel p-2 rounded-lg group cursor-pointer hover:scale-105 transition-all duration-200">
                {qrImageUrl ? (
                  <img src={qrImageUrl} alt="QR Code" className="w-16 h-16 rounded" />
                ) : (
                  <div className="w-16 h-16 rounded flex items-center justify-center bg-muted/50">
                    <QrCode className="h-8 w-8 text-muted-foreground/40" />
                  </div>
                )}
              </div>
            </div>
          </div>

          {asset.qr_code_id && (
            <div className="flex items-center justify-end pt-3 mt-3 border-t border-border/50">
              <span className="text-[10px] text-muted-foreground font-mono px-2 py-1 bg-muted/30 rounded">
                ID: {asset.qr_code_id}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Specifications Grid */}
      {coreSpecs.length > 0 && (
        <div className="grid grid-cols-2 gap-2 px-6 pb-4">
          {coreSpecs.map((spec, idx) => (
            <div
              key={idx}
              className="group relative overflow-hidden rounded-lg border border-border/50 bg-gradient-to-br from-muted/30 to-muted/10 p-3 hover:border-primary/30 hover:shadow-lg transition-all duration-300 animate-fade-in"
              style={{ animationDelay: `${idx * 40}ms` }}
            >
              <div className="flex items-center gap-2.5">
                <div className="flex-shrink-0 p-1.5 rounded-md bg-primary/10 group-hover:bg-primary/20 transition-colors">
                  <spec.icon className="h-3.5 w-3.5 text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-wide text-muted-foreground font-medium mb-0.5">
                    {spec.label}
                  </div>
                  <div className="font-semibold text-sm truncate text-foreground">
                    {spec.value}
                  </div>
                </div>
              </div>
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
